import {LOCALE_ID, NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {AppComponent} from './app.component';
import {InteractionType, IPublicClientApplication, PublicClientApplication} from '@azure/msal-browser';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorAuthRequest,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalRedirectComponent,
    MsalService
} from "@azure/msal-angular";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from "@angular/common/http";
import {AppRoutingModule} from './app-routing.module';
import {HomeComponent} from './pages/home/home.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatSidenavModule} from "@angular/material/sidenav";
import {MatIconModule} from "@angular/material/icon";
import {MatToolbarModule} from "@angular/material/toolbar";
import {MatListModule} from "@angular/material/list";
import {MatButtonModule} from "@angular/material/button";
import {DashboardComponent} from './pages/dashboard/dashboard.component';
import {EmployeeOverviewComponent} from './pages/employee/employee-overview/employee-overview.component';
import {
    DxAutocompleteModule,
    DxBulletModule,
    DxButtonModule,
    DxButtonGroupModule,
    DxChartModule,
    DxCheckBoxModule,
    DxDataGridModule,
    DxDateBoxModule,
    DxDateRangeBoxModule,
    DxDrawerModule,
    DxDropDownBoxModule,
    DxDropDownButtonModule,
    DxFileUploaderModule,
    DxFormModule,
    DxGalleryModule,
    DxHtmlEditorModule,
    DxListModule,
    DxMapModule,
    DxMenuModule,
    DxNumberBoxModule,
    DxPieChartModule,
    DxPolarChartModule,
    DxPopupModule,
    DxProgressBarModule,
    DxRadioGroupModule,
    DxResponsiveBoxModule,
    DxSchedulerModule,
    DxSelectBoxModule,
    DxTabsModule,
    DxTagBoxModule,
    DxTemplateModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxTreeListModule,
    DxTreeViewModule,
    DxValidationSummaryModule,
    DxValidatorModule,
    DxPopoverModule,
    DxTabPanelModule,
    DxDraggableModule,
    DxScrollViewModule,
    DxSwitchModule,
    DxTileViewModule,
    DxTooltipModule,
    DxAccordionModule
} from "devextreme-angular";
import {MatTooltipModule} from "@angular/material/tooltip";
import {CookieService} from 'ngx-cookie-service';
import {OrganisationUnitOverviewComponent} from './pages/organization-unit/organisation-unit-overview/organisation-unit-overview.component';
import {MatInputModule} from "@angular/material/input";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatSelectModule} from "@angular/material/select";
import {MatRadioModule} from "@angular/material/radio";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {MatTabsModule} from "@angular/material/tabs";
import {ToastrModule} from "ngx-toastr";
import {EnumKeysPipe} from './pipes/enum-keys.pipe';
import {MatCheckboxModule} from "@angular/material/checkbox";
import {EmployeeTableComponent} from './shared-components/employee-table/employee-table.component';
import {TrainingImageFallbackDirective} from './directives/training-image-fallback.directive';
import {NgxMatSelectSearchModule} from "ngx-mat-select-search";
import {OrganisationUnitTreeDropDownComponent} from './components/shared/organisation-unit-tree-drop-down/organisation-unit-tree-drop-down.component';
import {TrainingGroupDropDownComponent} from './components/shared/training-group-drop-down/training-group-drop-down.component';
import {OverlayModule} from "@angular/cdk/overlay";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {SpinnerOverlayComponent} from './shared-components/spinner-overlay/spinner-overlay.component';
import {AiLoaderOverlayComponent} from './shared-components/ai-loader-overlay/ai-loader-overlay.component';
import {GoogleMapsComponent} from './components/shared/google-maps/google-maps.component';
import {GoogleMapsModule} from "@angular/google-maps";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {EventLocationComponent} from './shared-components/event-location/event-location.component';
import {IntegrationsComponent} from './pages/integrations/integrations.component';
import {WebexComponent} from './pages/integrations/webex/webex.component';
import {TrainingOverviewComponent} from './pages/training/training-overview/training-overview.component';
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatNativeDateModule} from "@angular/material/core";
import {DatePipe, registerLocaleData} from "@angular/common";
import localeDe from "@angular/common/locales/de";
import localeDeExtra from "@angular/common/locales/extra/de";
import {AreTrainingDatesDifferentDaysPipe} from './pipes/are-training-dates-different-days.pipe';
import {FirstStartDatePipe} from './pipes/first-start-date.pipe';
import {LastEndDatePipe} from './pipes/last-end-date.pipe';
import {TrainingDatePipe} from './pipes/training-date.pipe';
import {HeaderComponent} from './components/header/header.component';
import {TrainingDateSelectionComponent} from './components/training-date-selection/training-date-selection.component';
import {CompletedEventTableComponent} from './components/completed-event-table/completed-event-table.component';
import {TrainingTypeDisplayPipe} from './pipes/training-type-display.pipe';
import {DownloadComponent} from './download/download.component';
import {MatProgressBarModule} from "@angular/material/progress-bar";
import {MatMenuModule} from "@angular/material/menu";
import {AddExternalCertificateDialogComponent} from './components/add-external-certificate-dialog/add-external-certificate-dialog.component';
import {ConfirmationDialogComponent} from './components/shared/confirmation-dialog/confirmation-dialog.component';
import {GotowebinarComponent} from './pages/integrations/gotowebinar/gotowebinar.component';
import {TrainingCalendarComponent} from './pages/training/training-calendar/training-calendar.component';
import {TrainingMessageComponent} from './pages/training/training-invite/training-message.component';
import {ElearningMessageComponent} from './pages/manage-elearning/elearning-invite/elearning-message.component';
import {MatChipsModule} from "@angular/material/chips";
import {InvitePersonSelectDialogComponent} from './invite-person-select-dialog/invite-person-select-dialog.component';
import {SelectablePersonComponent} from './shared-components/selectable-person/selectable-person.component';
import {IsTrainingDateDifferentDaysPipe} from './pipes/is-training-date-different-days.pipe';
import {TrainingDetailEventMemberComponent} from './components/training-detail-event-member/training-detail-event-member.component';
import {ImagePickerComponent} from './shared-components/image-picker/image-picker.component';
import {SideNavComponent} from './components/side-nav/side-nav.component';
import {PageTitleComponent} from './components/shared/page-title/page-title.component';
import {PageComponent} from './components/shared/page/page.component';
import {PanelComponent} from './components/shared/panel/panel.component';
import {TableComponent} from './components/shared/table/table.component';
import {EmployeeFormComponent} from './pages/employee/employee-form/employee-form.component';
import {OrganizationUnitFormComponent} from './pages/organization-unit/organization-unit-form/organization-unit-form.component';
import {ExternalContactsOverviewComponent} from './pages/external-contacts/external-contacts-overview/external-contacts-overview.component';
import {ExternalContactsFormComponent} from './pages/external-contacts/external-contacts-form/external-contacts-form.component';
import {MyTrainingsComponent} from './pages/training/my-trainings/my-trainings.component';
import {TrainingInfoComponent} from './pages/training/training-info/training-info.component'
import {CourseInfoComponent} from './pages/training/course-info/course-info.component'
import {TrainingCardComponent} from './components/shared/training-card/training-card.component'
import {LearningPathCardComponent} from './components/learning-path-card/learning-path-card.component'
import {ReadMoreComponent} from './components/read-more/read-more.component'
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {AdministrationComponent} from './pages/administration/administration.component';
import {TrainingGroupsDetailsComponent} from './pages/administration/training-groups-details/training-groups-details.component';
import {TrainingCategoriesDetailsComponent} from './pages/administration/training-categories-details/training-categories-details.component';
import {TimePickerComponent} from './components/shared/time-picker/time-picker.component';
import {TrainingFormComponent} from './pages/training/training-form/training-form.component';
import {ManageElearningOverviewComponent} from './pages/manage-elearning/manage-elearning-overview/manage-elearning-overview.component';
import {EditManageElearningComponent} from './pages/manage-elearning/edit-manage-elearning/edit-manage-elearning.component';
import {VideoUploaderComponent} from './components/shared/video-uploader/video-uploader.component';
import {TrainingEmployeePopupComponent} from './components/training-employee-popup/training-employee-popup.component';
import {TrainingTemplateFormComponent} from './pages/training-template/training-template-form/training-template-form.component';
import {GeneralSettingsComponent} from './pages/general-settings/general-settings.component';
import {AttendeesListComponent} from './components/shared/attendees-list/attendees-list.component';
import {TrainingCompleteComponent} from './pages/training/training-complete/training-complete.component';
import {TrainingFeedbackComponent} from './pages/training/training-feedback/training-feedback.component';
import {NgxPrintModule} from 'ngx-print';
import {ChangePasswordModalComponent} from './components/change-password-modal/change-password-modal.component';
import {TrainingOverviewPopupComponent} from './components/training-overview-popup/training-overview-popup.component';
import {TrainingCancelDialogComponent} from './pages/training/training-cancel-dialog/training-cancel-dialog.component';
import {AttendeeListComponent} from "./pages/training/training-complete/attendee-list/attendee-list.component";
import {CourseQuestionsModalComponent} from "./components/course-questions-modal/course-questions-modal.component";
import {Configuration} from "../assets/Configuration";
import {environment, msalConfig, protectedResources} from "../environments/environment";
import {CourseQuestionsComponent} from './pages/training/course-questions/course-questions.component';
import {CourseQuestionComponent} from './pages/training/course-questions/course-question/course-question.component';
import {FeedbackComponent} from './pages/feedback/feedback.component';
import {MyInvitationComponent} from "./pages/my-invitation/my-invitation.component";
import {AcceptInvitationModalComponent} from "./components/accept-invitation-modal/accept-invitation-modal.component";
import {TestComponent} from "./pages/test/test.component";
import {LoginComponent} from "./pages/login/login.component";
import {ComponentLoaderComponent} from "./components/shared/component-loader/component-loader.component";
import {ImportDataComponent} from "./pages/administration/import-data/import-data.component";
import {TrainingCatalogueOverviewComponent} from "./pages/training-catalogue/training-catalogue-overview/training-catalogue-overview.component";
import {TrainingCatalogueCardComponent} from "./components/training-catalogue-card/training-catalogue-card.component";
import {LearningPathsOverviewComponent} from "./pages/learning-paths/learning-paths-overview/learning-paths-overview.component";
import {TrainingCatalogueInfoComponent} from "./pages/training-catalogue/training-catalogue-info/training-catalogue-info.component";
import {LearningPathsInfoComponent} from "./pages/learning-paths/learning-paths-info/learning-paths-info.component";
import {MergeEmployeesModalComponent} from "./components/merge-employees-modal/merge-employees-modal.component";
import {MergeElerningsModalComponent} from "./components/merge-elernings-modal/merge-elernings-modal.component";
import {ArchiveElerningsModalComponent} from "./components/archive-elernings-modal/archive-elernings-modal.component";
import {IntegrateWbdModalComponent} from "./components/integrate-wbd-modal/integrate-wbd-modal.component";
import {ReportingIddComponent} from "./pages/reporting/reporting-idd/reporting-idd.component";
import {ReportingComplianceComponent} from "./pages/reporting/reporting-compliance/reporting-compliance.component";
import { debug } from 'console';
import { Router } from '@angular/router';
import * as Sentry from "@sentry/angular";
import { CreateMyCompetenceComponent } from './pages/manage-elearning/create-mycompetence/create-mycompetence.component';
import { CreateTenantComponent } from './pages/administration/create-tenant/create-tenant.component';
import { ConfirmTrainingParticipationComponent } from './pages/confirm-training-participation/confirm-training-participation.component';
import { ConfirmTrainingParticipationModalComponent } from './components/confirm-training-participation-modal/confirm-training-participation-modal.component';
import { DownloadQrCodeModalComponent } from './components/download-qr-code-modal/download-qr-code-modal.component';
import { ESignatureTooltipComponent } from './components/e-signature-tooltip/e-signature-tooltip.component';
import { RegistrationLinksOverviewComponent } from './pages/registration-links/registration-overview/registration-links-overview.component';
import { RegistrationLinkInfoComponent } from './pages/registration-links/registration-link-info/registration-link-info.component';
import { RegisterComponent } from './pages/register/register.component';
import { RegistrationModalComponent } from './components/registration-modal/registration-modal.component';
import { TickerCardComponent } from './components/ticker-card/ticker-card.component';
import { DateRangeModalComponent } from './components/date-range-modal/date-range-modal.component';
import { Top10RecordsWidgetComponent } from './pages/dashboard/widgets/top-10-records-widget/top-10-records-widget.component';
import { QualificationStatusWidgetComponent } from './pages/dashboard/widgets/qualification-status-widget/qualification-status-widget.component';
import { MyQualificationStatusWidgetComponent } from './pages/dashboard/widgets/my-qualification-status-widget/my-qualification-status-widget.component';
import { CompetencyMatrixWidgetComponent } from './pages/dashboard/widgets/competency-matrix-widget/competency-matrix-widget.component';
import { PersonCompetencyMatrixWidgetComponent } from './pages/dashboard/widgets/person-competency-matrix-widget/person-competency-matrix-widget.component';
import { TrainingGroupQualificationStatusWidgetComponent } from './pages/dashboard/widgets/training-group-qualification-status-widget/training-group-qualification-status-widget.component';
import { ComplianceCourseQualificationStatusWidgetComponent } from './pages/dashboard/widgets/compliance-course-qualification-status-widget/compliance-course-qualification-status-widget.component';
import { LearningPathStatusWidgetComponent } from './pages/dashboard/widgets/learning-path-status-widget/learning-path-status-widget.component';
import { TrendToTargetIddWidgetComponent } from './pages/dashboard/widgets/trend-to-target-idd-widget/trend-to-target-idd-widget.component';
import { ReportingWidgetComponent } from './pages/dashboard/widgets/reporting-widget/reporting-widget.component';
import { DashboardStatisticsWidgetComponent } from './pages/dashboard/widgets/dashboard-statistics-widget/dashboard-statistics-widget.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CurrentLearningPathStatusWidgetComponent } from './pages/dashboard/widgets/current-learning-path-status-widget/current-learning-path-status-widget.component';
import { DxSortableModule } from 'devextreme-angular';
import { Top10RecordsWidgetDemoComponent } from './pages/dashboard/demo-widgets/top-10-records-widget-demo/top-10-records-widget-demo.component';
import { CompetencyMatrixWidgetDemoComponent } from './pages/dashboard/demo-widgets/competency-matrix-widget-demo/competency-matrix-widget-demo.component';
import { ComplianceCourseQualificationStatusWidgetDemoComponent } from './pages/dashboard/demo-widgets/compliance-course-qualification-status-widget-demo/compliance-course-qualification-status-widget-demo.component';
import { CurrentLearningPathStatusWidgetDemoComponent } from './pages/dashboard/demo-widgets/current-learning-path-status-widget-demo/current-learning-path-status-widget-demo.component';
import { LearningPathStatusWidgetDemoComponent } from './pages/dashboard/demo-widgets/learning-path-status-widget-demo/learning-path-status-widget-demo.component';
import { MyQualificationStatusWidgetDemoComponent } from './pages/dashboard/demo-widgets/my-qualification-status-widget-demo/my-qualification-status-widget-demo.component';
import { PersonCompetencyMatrixWidgetDemoComponent } from './pages/dashboard/demo-widgets/person-competency-matrix-widget-demo/person-competency-matrix-widget-demo.component';
import { QualificationStatusWidgetDemoComponent } from './pages/dashboard/demo-widgets/qualification-status-widget-demo/qualification-status-widget-demo.component';
import { ReportingWidgetDemoComponent } from './pages/dashboard/demo-widgets/reporting-widget-demo/reporting-widget-demo.component';
import { TrainingGroupQualificationStatusWidgetDemoComponent } from './pages/dashboard/demo-widgets/training-group-qualification-status-widget-demo/training-group-qualification-status-widget-demo.component';
import { TrendToTargetIddWidgetDemoComponent } from './pages/dashboard/demo-widgets/trend-to-target-idd-widget-demo/trend-to-target-idd-widget-demo.component';
import { ScormPlayerComponent } from './pages/scorm-player/scorm-player.component';
import { TrainerCardComponent } from './components/shared/trainer-card/trainer-card.component';
import { LearningPathMessageComponent } from './pages/learning-paths/learning-path-invite/learning-path-message.component';
import { SubmitExternalCertificateModalComponent } from './components/submit-external-certificate-modal/submit-external-certificate-modal.component';
import { UpdateExternalCertificateRequestDialogComponent } from './components/update-external-certificate-request-dialog/update-external-certificate-request-dialog.component';
import { ReviewExternalCertificateDialogComponent } from './components/review-external-certificate-dialog/review-external-certificate-dialog.component';
import { SafePipe } from './pipes/safe.pipe';
import { FileViewerModalComponent } from './components/file-viewer-modal/file-viewer-modal.component';
import { DeclineApprovalRequestModalComponent } from './components/decline-approval-request-modal/decline-approval-request-modal.component';
import { SubscriptionsComponent } from './pages/subscriptions/subscriptions.component';
import { RequestSubscriptionModalComponent } from './components/request-subscription-modal/request-subscription-modal.component';
import { RemoveSubscriberModalComponent } from './components/remove-subscriber-modal/remove-subscriber-modal.component';
import { SubscriptionSettingsModalComponent } from './components/subscription-settings-modal/subscription-settings-modal.component';
import { ActivatePublishingModeModalComponent } from './components/activate-publishing-mode-modal/activate-publishing-mode-modal.component';
import { DeactivatePublishingModeModalComponent } from './components/deactivate-publishing-mode-modal/deactivate-publishing-mode-modal.component';
import { TrainerDetailsComponent } from './pages/trainer-details/trainer-details.component';
import { DeclineTrainerInvitationDialogComponent } from './components/decline-trainer-invitation-dialog/decline-trainer-invitation-dialog.component';
import { SelectAdditionalParticipantsModalComponent } from './components/select-additional-participants-modal/select-additional-participants-modal.component';
import { CreateProfileModalComponent } from './components/create-profile-modal/create-profile-modal.component';
import { MergePersonModalComponent } from './components/merge-person-modal/merge-person-modal.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivateUserModalComponent } from './components/activate-user-modal/activate-user-modal.component';
import { ActivateAllUsersModalComponent } from './components/activate-all-users-modal/activate-all-users-modal.component';
import { CopyInvitationLinkModalComponent } from './components/copy-invitation-link-modal/copy-invitation-link-modal.component';
import { ChannelTrainingParticipantsWidgetDemoComponent } from './pages/dashboard/demo-widgets/channel-training-participants-widget-demo/channel-training-participants-widget-demo.component';
import { ChannelTrainingParticipantsWidgetComponent } from './pages/dashboard/widgets/channel-training-participants-widget/channel-training-participants-widget.component';
import { RejectRequestModalComponent } from './components/reject-request-modal/reject-request-modal.component';
import { DisconnectSubscriptionModalComponent } from './components/disconnect-subscription-modal/disconnect-subscription-modal.component';
import { TrainingWaitingListModalComponent } from './components/training-waiting-list-modal/training-waiting-list-modal.component';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { ComplianceCourseDateSelectionComponent } from './components/compliance-course-date-selection/compliance-course-date-selection.component';
import { ManageComplianceOverviewComponent } from './pages/manage-compliance/manage-compliance-overview/manage-compliance-overview.component';
import { EditManageComplianceComponent } from './pages/manage-compliance/edit-manage-compliance/edit-manage-compliance.component';
import { ComplianceAssociatedCoursesComponent } from './pages/manage-compliance/edit-manage-compliance/compliance-associated-courses/compliance-associated-courses.component';
import { ComplianceCompetenceMatrixComponent } from './pages/manage-compliance/edit-manage-compliance/compliance-competence-matrix/compliance-competence-matrix.component';
import { ComplianceLocationQualificationStatusComponent } from './pages/manage-compliance/edit-manage-compliance/compliance-location-qualification-status/compliance-location-qualification-status.component';
import { CoursePersonsTableComponent } from './components/course-persons-table/course-persons-table.component';
import { LearningPathPersonsTableComponent } from './components/learning-path-persons-table/learning-path-persons-table.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { PendingRequestsWidgetDemoComponent } from './pages/dashboard/demo-widgets/pending-requests-widget-demo/pending-requests-widget-demo.component';
import { PendingRequestsWidgetComponent } from './pages/dashboard/widgets/pending-requests-widget/pending-requests-widget.component';
import { ActivityLogsComponent } from './pages/activity-logs/activity-logs.component';
import { LearningRecommendationsWidgetComponent } from './pages/dashboard/widgets/learning-recommendations-widget/learning-recommendations-widget.component';
import { TrainingGroupPersonsComponent } from './pages/administration/training-groups-details/training-group-persons/training-group-persons.component';
import { EditTrainingParticipationQuestionsComponent } from './components/edit-training-participation-questions/edit-training-participation-questions.component';
import { AnswerTrainingAdditionalQuestionsModalComponent } from './components/answer-training-additional-questions-modal/answer-training-additional-questions-modal.component';
import { DateSelectionComponent } from './components/shared/date-selection/date-selection.component';
import { TrainingParticipationAnswersComponent } from './components/training-participation-answers/training-participation-answers.component';
import { AnswerTrainingQuestionsDialogComponent } from './components/answer-training-questions-dialog/answer-training-questions-dialog.component';
import { ExpandableTextEditorComponent } from './components/shared/expandable-text-editor/expandable-text-editor.component';
import { OnboardingOverlayComponent } from './components/onboarding-overlay/onboarding-overlay.component';
import { TrainingStartDateChangedNotificationDialogComponent } from './components/training-start-date-changed-notification-dialog/training-start-date-changed-notification-dialog.component';
import { DeclineInvitationModalComponent } from './components/decline-invitation-modal/decline-invitation-modal.component';
import { TokenAcceptFlowComponent } from './components/token-accept-flow/token-accept-flow.component';
import { TrainingActionModalComponent } from './components/training-action-modal/training-action-modal.component';
import { ShareIddNumberModalComponent } from './components/share-idd-number-modal/share-idd-number-modal.component';
import { CompleteCourseManuallyModalComponent } from './components/complete-course-manually-modal/complete-course-manually-modal.component';
import { DxReportViewerModule } from 'devexpress-reporting-angular';
import { ReportViewerModalComponent } from './components/report-viewer-modal/report-viewer-modal.component';
import { BrokerConfirmationComponent } from './pages/broker-confirmation/broker-confirmation.component';
import { TextTemplateDetailsComponent } from './pages/administration/text-template-details/text-template-details.component';
import { SaveTextTemplateModalComponent } from './components/save-text-template-modal/save-text-template-modal.component';
import { TextTemplateSelectorComponent } from './components/text-template-selector/text-template-selector.component';
import { OrganisationUnitFooterSelectorComponent } from './components/organisation-unit-footer-selector/organisation-unit-footer-selector.component';
import { PublicTrainingOverviewComponent } from './pages/public-training-overview/public-training-overview.component';
import { PublicTrainingOverviewCardComponent } from './components/public-training-overview-card/public-training-overview-card.component';
import { AuthInterceptor } from './auth.interceptor';
import { RegistrationFieldConfigComponent } from './components/registration-field-config/registration-field-config.component';
import { StandardCategorySelectorModalComponent } from './components/standard-category-selector-modal/standard-category-selector-modal.component';
import { EmployeeVisibilityComponent } from './components/employee-visibility/employee-visibility.component';
import { PublicationModeSelectionComponent } from './components/publication-mode-selection/publication-mode-selection.component';
import { ByInvitationAccessSectionComponent } from './components/by-invitation-access-section/by-invitation-access-section.component';
import { TargetGroupSectionComponent } from './components/target-group-section/target-group-section.component';
import { PublishToCatalogueSectionComponent } from './components/publish-to-catalogue-section/publish-to-catalogue-section.component';
import { EventPublicationSettingsComponent } from './components/event-publication-settings/event-publication-settings.component';
import { TenantEventPublishingSettingsFormComponent } from './components/tenant-event-publishing-settings-form/tenant-event-publishing-settings-form.component';
import { PublishToSubscribersSectionComponent } from './components/publish-to-subscribers-section/publish-to-subscribers-section.component';
import { PublishingVisibilityComponent } from './components/publishing-visibility/publishing-visibility.component';
import { PublishingModeRequiredModalComponent } from './components/publishing-mode-required-modal/publishing-mode-required-modal.component';
import { ProvidedTenantSubscriptionModalComponent } from './components/provided-tenant-subscription-modal/provided-tenant-subscription-modal.component';
import { MarketingChannelsComponent } from './components/marketing-channels/marketing-channels.component';
import { TrainerGroupComponent } from './components/shared/trainer-group/trainer-group.component';
import { ManageLearningPathOverviewComponent } from './pages/manage-learning-path/manage-learning-path-overview/manage-learning-path-overview.component';
import { EditLearningPathComponent } from './pages/manage-learning-path/edit-learning-path/edit-learning-path.component';
import { StandardCategoriesOverviewComponent } from './pages/standard-categories-admin/standard-categories-overview/standard-categories-overview.component';
import { AdminLoginComponent } from './components/admin-login/admin-login.component';
import { EditStandardCategoryComponent } from './pages/standard-categories-admin/edit-standard-category/edit-standard-category.component';
import { CatalogueSelectorComponent } from './components/catalogue-selector/catalogue-selector.component';
import { StandardCatalogueSelectorComponent } from './components/standard-catalogue-selector/standard-catalogue-selector.component';
import { SubscriberSelectorComponent } from './components/subscriber-selector/subscriber-selector.component';
import { ContentUnlockedModalComponent } from './components/content-unlocked-modal/content-unlocked-modal.component';
import { IddEscalationThresholdsComponent } from './pages/general-settings/idd-escalation-thresholds/idd-escalation-thresholds.component';
import { InvalidInvitationComponent } from './components/invalid-invitation/invalid-invitation.component';
import { VideoPlayerComponent } from './components/shared/video-player/video-player.component';
import { BrokerRegisterComponent } from './pages/broker-registration/broker-register/broker-register.component';
import { BrokerRegistrationConfirmationComponent } from './pages/broker-registration/broker-registration-confirmation/broker-registration-confirmation.component';
import { CorporateRegistrationApprovalComponent } from './pages/corporate-registration-approval/corporate-registration-approval.component';
import { RegistrationLinkQuestionsComponent } from './pages/registration-links/registration-link-info/registration-link-questions/registration-link-questions.component';
import { RegistrationFlowComponent } from './pages/registration-links/registration-link-info/registration-flow/registration-flow.component';
import { RegistrationFlowOnboardingComponent } from './components/registration-flow/registration-flow-onboarding/registration-flow-onboarding.component';
import { TenantVideoComponent } from './components/tenant-video/tenant-video.component';

registerLocaleData(localeDe, 'de', localeDeExtra);

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */

export function MSALInstanceFactory(): IPublicClientApplication {
    const config = {...msalConfig};

    //TODO maybe microsoft fixed a bug and it works now without a custom app registration for SSO

    // const subDomain = window.location.hostname.split('.')[0];
    // if(environment.ssoSubDomains.some(x => x.toLowerCase() == subDomain.toLowerCase())) {
    //     config.auth.clientId = environment.ssoClientId;
    // }
    return new PublicClientApplication(config);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();

    let adb2cTemplate = environment.defaultAdb2cTemplate;
    if (window.location.hostname.endsWith(".skeddle.com")) {
        adb2cTemplate = window.location.hostname.split(".")[0];
    }

    protectedResourceMap.set(
        Configuration.getApiBaseUrl(),
        protectedResources.scopes
    );

    return {
        interactionType: InteractionType.Redirect,
        authRequest: ({
            scopes: ["profile"]
        } as MsalInterceptorAuthRequest),
        protectedResourceMap
    };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: ["profile"]
        }
    };
}

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

export function initializeMsal(msalInstance: IPublicClientApplication) {
  return async () => {
    await msalInstance.initialize();
  };
}

@NgModule({
    declarations: [
        AppComponent,
        HeaderComponent,
        HomeComponent,
        DashboardComponent,
        EmployeeOverviewComponent,
        OrganisationUnitOverviewComponent,
        EnumKeysPipe,
        EmployeeTableComponent,
        TrainingImageFallbackDirective,
        OrganisationUnitTreeDropDownComponent,
        TrainingGroupDropDownComponent,
        SpinnerOverlayComponent,
        AiLoaderOverlayComponent,
        ComponentLoaderComponent,
        GoogleMapsComponent,
        EventLocationComponent,
        IntegrationsComponent,
        WebexComponent,
        TrainingOverviewComponent,
        AreTrainingDatesDifferentDaysPipe,
        FirstStartDatePipe,
        LastEndDatePipe,
        TrainingDatePipe,
        TrainingDateSelectionComponent,
        CompletedEventTableComponent,
        TrainingTypeDisplayPipe,
        SafePipe,
        DownloadComponent,
        AddExternalCertificateDialogComponent,
        ConfirmationDialogComponent,
        GotowebinarComponent,
        TrainingCalendarComponent,
        TrainingMessageComponent,
        ElearningMessageComponent,
        InvitePersonSelectDialogComponent,
        IsTrainingDateDifferentDaysPipe,
        TrainingDetailEventMemberComponent,
        ImagePickerComponent,
        SideNavComponent,
        PageTitleComponent,
        PageComponent,
        PanelComponent,
        TableComponent,
        EmployeeFormComponent,
        OrganizationUnitFormComponent,
        ExternalContactsOverviewComponent,
        ExternalContactsFormComponent,
        MyTrainingsComponent,
        TrainingInfoComponent,
        CourseInfoComponent,
        LearningPathCardComponent,
        TrainingCardComponent,
        ReadMoreComponent,
        AdministrationComponent,
        TrainingGroupsDetailsComponent,
        TrainingCategoriesDetailsComponent,
        TimePickerComponent,
        TrainingFormComponent,
        ManageElearningOverviewComponent,
        EditManageElearningComponent,
        VideoUploaderComponent,
        TrainingEmployeePopupComponent,
        TrainingTemplateFormComponent,
        GeneralSettingsComponent,
        AttendeesListComponent,
        TrainingCompleteComponent,
        TrainingFeedbackComponent,
        ChangePasswordModalComponent,
        TrainingOverviewPopupComponent,
        TrainingCancelDialogComponent,
        CourseQuestionsModalComponent,
        CourseQuestionsComponent,
        CourseQuestionComponent,
        FeedbackComponent,
        MyInvitationComponent,
        AcceptInvitationModalComponent,
        TestComponent,
        LoginComponent,
        ImportDataComponent,
        TrainingCatalogueOverviewComponent,
        TrainingCatalogueCardComponent,
        LearningPathsOverviewComponent,
        TrainingCatalogueInfoComponent,
        LearningPathsInfoComponent,
        MergeEmployeesModalComponent,
        MergeElerningsModalComponent,
        ArchiveElerningsModalComponent,
        TrainingActionModalComponent,
        IntegrateWbdModalComponent,
        ReportingIddComponent,
        ReportingComplianceComponent,
        CreateMyCompetenceComponent,
        CreateTenantComponent,
        ConfirmTrainingParticipationComponent,
        ConfirmTrainingParticipationModalComponent,
        DownloadQrCodeModalComponent,
        RegistrationLinksOverviewComponent,
        RegistrationLinkInfoComponent,
        RegisterComponent,
        RegistrationModalComponent,
        TickerCardComponent,
        DateRangeModalComponent,
        Top10RecordsWidgetComponent,
        QualificationStatusWidgetComponent,
        MyQualificationStatusWidgetComponent,
        CompetencyMatrixWidgetComponent,
        PersonCompetencyMatrixWidgetComponent,
        TrainingGroupQualificationStatusWidgetComponent,
        ComplianceCourseQualificationStatusWidgetComponent,
        LearningPathStatusWidgetComponent,
        TrendToTargetIddWidgetComponent,
        ReportingWidgetComponent,
        DashboardStatisticsWidgetComponent,
        CurrentLearningPathStatusWidgetComponent,
        Top10RecordsWidgetDemoComponent,
        CompetencyMatrixWidgetDemoComponent,
        ComplianceCourseQualificationStatusWidgetDemoComponent,
        CurrentLearningPathStatusWidgetDemoComponent,
        LearningPathStatusWidgetDemoComponent,
        MyQualificationStatusWidgetDemoComponent,
        PersonCompetencyMatrixWidgetDemoComponent,
        QualificationStatusWidgetDemoComponent,
        ReportingWidgetDemoComponent,
        TrainingGroupQualificationStatusWidgetDemoComponent,
        TrendToTargetIddWidgetDemoComponent,
        ScormPlayerComponent,
        TrainerCardComponent,
        LearningPathMessageComponent,
        SubmitExternalCertificateModalComponent,
        PendingRequestsWidgetComponent,
        PendingRequestsWidgetDemoComponent,
        UpdateExternalCertificateRequestDialogComponent,
        ReviewExternalCertificateDialogComponent,
        FileViewerModalComponent,
        DeclineApprovalRequestModalComponent,
        SubscriptionsComponent,
        RequestSubscriptionModalComponent,
        RemoveSubscriberModalComponent,
        SubscriptionSettingsModalComponent,
        ActivatePublishingModeModalComponent,
        DeactivatePublishingModeModalComponent,
        TrainerDetailsComponent,
        DeclineTrainerInvitationDialogComponent,
        SelectAdditionalParticipantsModalComponent,
        CreateProfileModalComponent,
        AttendeeListComponent,
        SelectablePersonComponent,
        ESignatureTooltipComponent,
        MergePersonModalComponent,
        ActivateUserModalComponent,
        ActivateAllUsersModalComponent,
        CopyInvitationLinkModalComponent,
        ChannelTrainingParticipantsWidgetComponent,
        ChannelTrainingParticipantsWidgetDemoComponent,
        RejectRequestModalComponent,
        DisconnectSubscriptionModalComponent,
        TrainingWaitingListModalComponent,
        ComplianceAssociatedCoursesComponent,
        ComplianceCompetenceMatrixComponent,
        ComplianceLocationQualificationStatusComponent,
        ComplianceCourseDateSelectionComponent,
        ManageComplianceOverviewComponent,
        EditManageComplianceComponent,
        CoursePersonsTableComponent,
        LearningPathPersonsTableComponent,
        ActivityLogsComponent,
        LearningRecommendationsWidgetComponent,
        TrainingGroupPersonsComponent,
        EditTrainingParticipationQuestionsComponent,
        AnswerTrainingAdditionalQuestionsModalComponent,
        DateSelectionComponent,
        TrainingParticipationAnswersComponent,
        AnswerTrainingQuestionsDialogComponent,
        ExpandableTextEditorComponent,
        OnboardingOverlayComponent,
        CompleteCourseManuallyModalComponent,
        TrainingStartDateChangedNotificationDialogComponent,
        DeclineInvitationModalComponent,
        TokenAcceptFlowComponent,
        ShareIddNumberModalComponent,
        ReportViewerModalComponent,
        BrokerConfirmationComponent,
        TextTemplateDetailsComponent,
        SaveTextTemplateModalComponent,
        TextTemplateSelectorComponent,
        OrganisationUnitFooterSelectorComponent,
        PublicTrainingOverviewComponent,
        PublicTrainingOverviewCardComponent,
        TenantEventPublishingSettingsFormComponent,
        RegistrationFieldConfigComponent,
        StandardCategorySelectorModalComponent,
        EmployeeVisibilityComponent,
        PublicationModeSelectionComponent,
        ByInvitationAccessSectionComponent,
        TargetGroupSectionComponent,
        PublishToCatalogueSectionComponent,
        EventPublicationSettingsComponent,
        PublishToSubscribersSectionComponent,
        PublishingVisibilityComponent,
        PublishingModeRequiredModalComponent,
        ProvidedTenantSubscriptionModalComponent,
        MarketingChannelsComponent,
        TrainerGroupComponent,
        ManageLearningPathOverviewComponent,
        EditLearningPathComponent,
        StandardCategoriesOverviewComponent,
        AdminLoginComponent,
        EditStandardCategoryComponent,
        CatalogueSelectorComponent,
        StandardCatalogueSelectorComponent,
        SubscriberSelectorComponent,
        ContentUnlockedModalComponent,
        IddEscalationThresholdsComponent,
        InvalidInvitationComponent,
        VideoPlayerComponent,
        BrokerRegisterComponent,
        BrokerRegistrationConfirmationComponent,
        CorporateRegistrationApprovalComponent,
        RegistrationLinkQuestionsComponent,
        RegistrationFlowComponent,
        RegistrationFlowOnboardingComponent,
        TenantVideoComponent
    ],
    exports: [OrganisationUnitTreeDropDownComponent],
    bootstrap: [AppComponent, MsalRedirectComponent],
    imports: [
        BrowserModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        MsalModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MatSidenavModule,
        MatIconModule,
        MatToolbarModule,
        MatListModule,
        MatButtonModule,
        MatTooltipModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        DxButtonModule,
        DxButtonGroupModule,
        DxBulletModule,
        DxPopupModule,
        MatRadioModule,
        MatButtonToggleModule,
        DxHtmlEditorModule,
        DxMapModule,
        MatTabsModule,
        ToastrModule.forRoot(),
        MatCheckboxModule,
        OverlayModule,
        MatProgressSpinnerModule,
        NgxMatSelectSearchModule,
        DxTreeListModule,
        DxTreeViewModule,
        DxDataGridModule,
        DxTemplateModule,
        DxTextAreaModule,
        DxDropDownBoxModule,
        DxDropDownButtonModule,
        DxTextBoxModule,
        DxSchedulerModule,
        DxDrawerModule,
        DxCheckBoxModule,
        DxResponsiveBoxModule,
        GoogleMapsModule,
        MatAutocompleteModule,
        DxFileUploaderModule,
        DxFormModule,
        DxProgressBarModule,
        DxSelectBoxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        DxDateBoxModule,
        DxAutocompleteModule,
        MatProgressBarModule,
        MatMenuModule,
        DxMenuModule,
        DxTabsModule,
        FormsModule,
        MatChipsModule,
        DxNumberBoxModule,
        DxRadioGroupModule,
        DxListModule,
        DxTagBoxModule,
        DxChartModule,
        DxPieChartModule,
        DxPolarChartModule,
        NgxPrintModule,
        DxGalleryModule,
        DxDateRangeBoxModule,
        DxValidatorModule,
        DxValidationSummaryModule,
        DxToolbarModule,
        DxPopoverModule,
        DxTabPanelModule,
        DragDropModule,
        DxSortableModule,
        DxDraggableModule,
        DxScrollViewModule,
        DxSwitchModule,
        MatSlideToggleModule,
        PdfViewerModule,
        DxTileViewModule,
        DxTooltipModule,
        MatPaginatorModule,
        DxAccordionModule,
        DxReportViewerModule
        ], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        }, {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeMsal,
            deps: [MSAL_INSTANCE],
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useValue: "de-DE"
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        TrainingTypeDisplayPipe,
        SafePipe,
        CookieService,
        DatePipe,
        SafePipe,
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport())
    ] })
export class AppModule {
    constructor() {
    }
}
